import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useRequest } from '@frontend/api';

import { MFG_QUERY_KEYS } from './constants';
import type {
  AdskMfgQueryResource,
  FusionFolder,
  AdskMfgParams,
  AdskMfgQueryResponse,
  AdskMfgInfiniteQueryFunction,
} from './types';
import { adskRequest, isAdskMfgError } from './utils';

const GRAPHQL_QUERY = {
  operationName: 'GetFoldersByFolder',
  query: `query GetFoldersByFolder($hubId: ID!, $folderId: ID!, $pagination: PaginationInput) {
        foldersByFolderInHub(hubId: $hubId, folderId: $folderId, pagination: $pagination) {
            pagination {
                cursor
            }
            results {
                __typename
                id
                name
            }
        }
    }`,
};

type DataType = {
  foldersByFolderInHub: AdskMfgQueryResource<FusionFolder[]>;
};

type Params = AdskMfgParams & {
  hubId: string | null;
  folderId: string | null;
};

type ListFoldersByFolderQueryKey = readonly [
  typeof MFG_QUERY_KEYS.LIST_FOLDERS_BY_FOLDER,
  string,
  string,
];

const getListFoldersByFolderQueryKey = (
  hubId: string,
  folderId: string,
): ListFoldersByFolderQueryKey => [
  MFG_QUERY_KEYS.LIST_FOLDERS_BY_FOLDER,
  hubId,
  folderId,
];

export const useListFoldersByFolder: AdskMfgInfiniteQueryFunction<
  AdskMfgQueryResponse<DataType>,
  [Params],
  ListFoldersByFolderQueryKey,
  FusionFolder
> = (
  {
    accessToken,
    refreshAccess,
    hubId,
    folderId,
    pagination = { cursor: null },
  },
  { queryOptions } = {},
) => {
  const queryClient = useQueryClient();
  const request = useRequest();
  const results = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- adskRequest should be built with a hook, pagination should be added to the query key
    queryKey: getListFoldersByFolderQueryKey(hubId ?? '', folderId ?? ''),
    queryFn: ({ signal, pageParam }) =>
      adskRequest<DataType>(
        accessToken ?? '',
        {
          ...GRAPHQL_QUERY,
          variables: {
            hubId,
            folderId,
            pagination: { ...pagination, cursor: pageParam },
          },
        },
        request,
        { init: { signal } },
        (data) => !!data.foldersByFolderInHub,
        (msg) =>
          `Failed to list folders for folder ${folderId} in hub ${hubId}: ${msg}`,
      ),
    enabled: !!accessToken && !!hubId && !!folderId,
    getNextPageParam: (currentPage) =>
      currentPage.data.foldersByFolderInHub.pagination.cursor === null
        ? null
        : currentPage.data.foldersByFolderInHub.pagination.cursor,
    ...queryOptions,
    onError: async (error) => {
      // If not an ADSK error, bail
      if (!isAdskMfgError(error)) return;

      // Trigger refresh on 401s
      if (error.statusCode === 401) {
        await refreshAccess();

        // Invalidate query to trigger a new request
        queryClient.invalidateQueries({
          queryKey: getListFoldersByFolderQueryKey(hubId ?? '', folderId ?? ''),
        });
        return;
      }

      // Run the passed in onError if provided
      queryOptions?.onError?.(error);
    },
  });

  const flatData = useMemo(
    () =>
      results?.data?.pages?.flatMap(
        (page) => page.data.foldersByFolderInHub.results,
      ),
    [results?.data?.pages],
  );

  const flatErrors = useMemo(
    () => results?.data?.pages?.flatMap((page) => page?.errors ?? []),
    [results?.data?.pages],
  );

  return { ...results, flatData, flatErrors };
};
